import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { DEAL_STATUSES, STATUS_META, type DealStatus } from '@accel/shared';
import { api } from '../lib/api';
import type { DealSummary, Paged } from '../lib/types';
import {
  Card,
  EmptyState,
  ErrorNote,
  Money,
  PageHead,
  PartyCell,
  Spinner,
  StatusTag,
  daysUntil,
  formatDate,
} from '../components/ui';
import { DealDrawer } from './DealDrawer';
import { useAuth } from '../auth/AuthContext';

export function DealsPage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(null);

  const statusParam = params.get('status') ?? '';
  const search = params.get('search') ?? '';
  const page = Number(params.get('page') ?? 1);

  const query = useQuery({
    queryKey: ['deals', statusParam, search, page],
    queryFn: () => {
      const qs = new URLSearchParams({ page: String(page), pageSize: '25', sort: 'dueDate', order: 'asc' });
      if (statusParam) qs.set('status', statusParam);
      if (search) qs.set('search', search);
      return api.get<Paged<DealSummary>>(`/deals?${qs}`);
    },
  });

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Any filter change invalidates the current page number.
    if (key !== 'page') next.delete('page');
    setParams(next);
  }

  const title = statusParam
    ? (STATUS_META[statusParam.split(',')[0] as DealStatus]?.label ?? 'Deals')
    : 'All deals';

  return (
    <>
      <PageHead
        crumb="Receivables"
        title={title}
        sub={
          query.data
            ? `${query.data.meta.total} deal${query.data.meta.total === 1 ? '' : 's'} visible to your role`
            : undefined
        }
        actions={
          can('deal:create') ? (
            <Link className="btn btn-primary" to="/deals/new">
              Submit a receivable
            </Link>
          ) : null
        }
      />

      <Card padded={false}>
        <div className="card-head" style={{ gap: 12 }}>
          <input
            className="select"
            style={{ minWidth: 240, padding: '8px 12px' }}
            placeholder="Search reference, supplier or payer"
            defaultValue={search}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setParam('search', (e.target as HTMLInputElement).value);
            }}
          />
          <select
            className="select"
            value={statusParam}
            onChange={(e) => setParam('status', e.target.value)}
          >
            <option value="">Every status</option>
            {DEAL_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_META[s].label}
              </option>
            ))}
          </select>
          {statusParam || search ? (
            <button className="btn btn-ghost" onClick={() => setParams(new URLSearchParams())}>
              Clear filters
            </button>
          ) : null}
        </div>

        <ErrorNote error={query.error} />

        {query.isLoading ? (
          <Spinner label="Loading deals" />
        ) : !query.data?.data.length ? (
          <EmptyState title="No deals match" sub="Try clearing the filters." />
        ) : (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Supplier</th>
                  <th>Payer</th>
                  <th className="r">Face value</th>
                  <th className="r">Advance</th>
                  <th>Due</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((deal) => {
                  const days = daysUntil(deal.dueDate);
                  return (
                    <tr key={deal.id} onClick={() => setSelected(deal.id)}>
                      <td>
                        <span className="cell-t1">{deal.reference}</span>
                        <span className="cell-t2" style={{ display: 'block' }}>
                          {deal.type === 'ORDER' ? 'Order finance' : deal.terms}
                        </span>
                      </td>
                      <td>
                        <PartyCell party={deal.sme} />
                      </td>
                      <td>
                        <PartyCell party={deal.payer} />
                      </td>
                      <td className="r">
                        <Money minor={deal.faceMinor} currency={deal.currency} />
                      </td>
                      <td className="r">
                        <Money minor={deal.advanceMinor} currency={deal.currency} />
                        {deal.advanceBps ? (
                          <span className="cell-t2" style={{ display: 'block' }}>
                            {(deal.advanceBps / 100).toFixed(0)}%
                          </span>
                        ) : null}
                      </td>
                      <td>
                        {formatDate(deal.dueDate)}
                        <span
                          className="cell-t2"
                          style={{ display: 'block', color: days < 0 ? 'var(--rose)' : undefined }}
                        >
                          {days < 0 ? `${Math.abs(days)}d overdue` : `in ${days}d`}
                        </span>
                      </td>
                      <td>
                        <StatusTag status={deal.status} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {query.data && query.data.meta.totalPages > 1 ? (
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '13px 18px',
              borderTop: '1px solid var(--line-2)',
            }}
          >
            <button
              className="btn btn-ghost"
              disabled={page <= 1}
              onClick={() => setParam('page', String(page - 1))}
            >
              Previous
            </button>
            <span className="cell-t2">
              Page {query.data.meta.page} of {query.data.meta.totalPages}
            </span>
            <button
              className="btn btn-ghost"
              disabled={page >= query.data.meta.totalPages}
              onClick={() => setParam('page', String(page + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </Card>

      <DealDrawer dealId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
